import { useCallback, useState } from 'react'
import { newId } from '@/lib/ids'
import type { TagEntry } from '../../commands'
import { mintLot } from '../../data/actions'
import { guardFor } from '../../data/useGate'
import { bagNet } from '../../model/figures'
import { bagWeight, draftPayloads, TYPED_SACK_CODE, v1Counts } from '../../model/delivery'
import { useWeightEntry } from '../../pages/delivery/hooks'
import {
  Band,
  Btn,
  Cta,
  Footer,
  GradeTiles,
  Grow,
  Icon,
  ItemRow,
  Keypad,
  Readout,
  SheetActions,
  Strip,
  Tick,
  V1Screen,
  V1Sheet,
  WaitCta,
  Warn,
} from '../kit'
import { C, speak, typeKey } from '../style'
import { LiveScanner, Refused, TypeCodeSheet } from '../scan'
import { useCheckedScan } from '../scanCheck'
import type { V1IconName } from '../sprite'
import { tr, kg, shortCode, sentences, sackName } from '../text'
import { Rich } from '../Rich'
import { StepHeader } from './shared'
import { useV1 } from './ctx'
import { OptionalPhoto } from '../OptionalPhoto'

/** kilos typed on the keypad ("49.4") → grams; null while empty. */
const toGrams = (s: string) => (s === '' || s === '.' ? null : Math.round(Number(s) * 1000))

function useLiveBags() {
  const { d } = useV1()
  return d.bags.filter((b) => !b.withdrawn)
}

/** F4.6 — टैग लगाएँ: the live scanner for the next bag's tag. */
export function TagScanScreen({
  onBack,
  onScanned,
  onDone,
}: {
  onBack: () => void
  onScanned: (s: { payload: string; entry: TagEntry }) => void
  onDone: () => void
}) {
  const { d, dispatch, roster, session, used, otherCodes } = useV1()
  const live = useLiveBags()
  const last = live.at(-1)
  const [err, setErr] = useState<string | null>(null)
  const [sheet, setSheet] = useState<null | 'type' | 'fix' | 'all'>(null)
  const guard = guardFor(session, roster, used, 'BAG', draftPayloads(d), otherCodes)
  const check = useCheckedScan({
    guard,
    onAccept: (payload, entry) => onScanned({ payload, entry }),
    onRefuse: (scan) => dispatch({ type: 'refuse_scan', scan }),
  })
  return (
    <V1Screen
      main={{ gap: 9 }}
      header={<StepHeader step="tag" icon="tag" title={tr('gate.v1.tag.title')} onBack={onBack} />}
      footer={
        <Footer>
          {live.length > 0 ? (
            <Cta label={tr('gate.v1.tag.allDone')} icon="next" onClick={onDone} />
          ) : (
            <WaitCta>{tr('gate.v1.tag.noneYet')}</WaitCta>
          )}
        </Footer>
      }
      overlay={
        sheet === 'type' ? (
          <TypeCodeSheet
            title={tr('gate.v1.tag.typeTitle')}
            check={check}
            onClose={() => setSheet(null)}
          />
        ) : sheet === 'fix' ? (
          <FixLastSheet onClose={() => setSheet(null)} />
        ) : sheet === 'all' ? (
          <AllBagsSheet onClose={() => setSheet(null)} />
        ) : null
      }
    >
      {last && (
        // The last bag saved; tap to see every bag of this farmer.
        <button
          type="button"
          role="status"
          onClick={() => setSheet('all')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px',
            borderRadius: 12,
            border: 'none',
            background: C.ok,
            color: '#ffffff',
            fontSize: 17,
            fontWeight: 700,
            fontFamily: "'Mukta', sans-serif",
            textAlign: 'left',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          <Icon name="ok" size={22} sw={3} />
          <span style={{ flexGrow: 1, minWidth: 0 }}>
            {tr('gate.v1.tag.lastSaved', { n: live.length, kg: kg(bagNet(last)) })}
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            {tr('gate.v1.tag.seeAll')}
            <Icon name="next" size={20} sw={2.6} />
          </span>
        </button>
      )}
      <LiveScanner icon="tag" hint={tr('gate.v1.tag.hold')} check={check} onError={setErr} />
      {err && <Refused text={err} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
        <Btn icon="keypad" onClick={() => setSheet('type')}>
          {tr('gate.v1.tag.type')}
        </Btn>
        <Btn icon="pencil" disabled={live.length === 0} onClick={() => setSheet('fix')}>
          {tr('gate.v1.tag.fixLast')}
        </Btn>
      </div>
    </V1Screen>
  )
}

/** Every bag of this farmer so far: its number, tag, Good or Red tie, sack and weights. */
function AllBagsSheet({ onClose }: { onClose: () => void }) {
  const { d, roster } = useV1()
  const live = useLiveBags()
  const total = (red: boolean) => {
    const bags = live.filter((b) => b.red === red)
    return { count: bags.length, kg: kg(bags.reduce((s, b) => s + bagNet(b), 0)) }
  }
  const good = total(false)
  const reds = total(true)
  return (
    <V1Sheet title={tr('gate.v1.tag.allTitle', { count: live.length })} onClose={onClose} top={80}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', fontSize: 16, fontWeight: 700 }}>
        <span>{tr('gate.v1.weigh.bandGood', good)}</span>
        {reds.count > 0 && <span>· {tr('gate.v1.weigh.bandRed', reds)}</span>}
      </div>
      {live.map((b, i) => {
        const w = bagWeight(b)
        const sack = b.sack_name ?? sackName(roster.sackTypes, b.sack_type_id)
        return (
          <ItemRow
            key={b.id}
            padding="8px 12px"
            border={b.red ? C.bad : C.rule}
            lead={
              <b
                style={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  borderRadius: 18,
                  background: b.red ? C.badWash : C.okWash,
                  color: b.red ? C.bad : C.ok,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 17,
                }}
              >
                {i + 1}
              </b>
            }
            titleSize={17}
            title={`${kg(bagNet(b))} ${tr('gate.v1.unit.kg')} · ${tr(b.red ? 'gate.v1.grade.red' : 'gate.v1.grade.good')}`}
            line={
              <span style={{ fontSize: 14, color: C.muted }}>
                {[
                  `${tr('gate.v1.tag.tagNo')} ${shortCode(b.qr_payload)}`,
                  sack,
                  w ? tr('gate.v1.tag.grossLine', { kg: kg(w.gross_g) }) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            }
          />
        )
      })}
      {d.bags.length > live.length && (
        <Strip icon="undo">
          {tr('gate.v1.tag.withdrawn', { count: d.bags.length - live.length })}
        </Strip>
      )}
    </V1Sheet>
  )
}

/** F4.6 — तौल लिखें: Good or Red tie, the sack (remembered), the weight on the keypad. */
export function WeighScreen({
  scanned,
  onBack,
  onSaved,
  onSeePage,
}: {
  scanned: { payload: string; entry: TagEntry }
  onBack: () => void
  onSaved: () => void
  /** Paper day: the page photo. */
  onSeePage?: () => void
}) {
  const { d, dispatch, roster, stamp } = useV1()
  const paper = d.capture_mode === 'PAPER_BACKFILL'
  const live = useLiveBags()
  const { good, red: reds } = v1Counts(d)
  const [red, setRed] = useState<boolean | null>(null)
  const [sack, setSack] = useState<string | null>(d.last_sack_type_id ?? null)
  const [text, setText] = useState('')
  const [sheet, setSheet] = useState(false)
  const [saving, setSaving] = useState(false)
  /** Live evidence of the scale display for this bag's weight. */
  const [scalePhoto, setScalePhoto] = useState<string | null>(null)
  const photoRequired = d.reading_photos_required === true
  const w = useWeightEntry(roster.config.weight_min_g, roster.config.weight_max_g)
  const setW = w.setValue
  // A sack not on the kit list: its name and weight typed here (the next bag starts with it).
  const typedType = roster.sackTypes.find((s) => s.code === TYPED_SACK_CODE) ?? null
  const [custom, setCustom] = useState<TypedSack | null>(() => {
    const last = d.bags.at(-1)
    return typedType && last?.sack_name && last.sack_type_id === typedType.id
      ? { name: last.sack_name, tare_g: last.tare_g }
      : null
  })
  const sackType =
    custom && typedType
      ? { id: typedType.id, tare_g: custom.tare_g }
      : (roster.sackTypes.find((s) => s.id === sack && s.code !== TYPED_SACK_CODE) ?? null)
  const grams = toGrams(text)
  const net = grams !== null && sackType ? grams - sackType.tare_g : null
  const n = live.length + 1
  const netOf = (isRed: boolean) =>
    live.filter((b) => b.red === isRed).reduce((s, b) => s + bagNet(b), 0)

  const onKey = useCallback(
    (k: string) => {
      setText((c) => {
        const next = typeKey(c, k, { decimals: 2, max: 6 })
        setW(toGrams(next))
        return next
      })
    },
    [setW],
  )

  const save = async () => {
    if (red === null || !sackType || grams === null || net === null || net <= 0) return
    if (photoRequired && !scalePhoto) return
    if (w.askAgain()) {
      setText('')
      return
    }
    if (!w.ready) return
    setSaving(true)
    try {
      const at = stamp()
      const both = red ? good > 0 : reds > 0
      const conv_lot = both && !d.conv_lot ? await mintLot(at) : undefined
      dispatch({
        type: 'tag_bag',
        red,
        conv_lot,
        bag: {
          id: newId(),
          qr_payload: scanned.payload,
          entry: scanned.entry,
          sack_type_id: sackType.id,
          tare_g: sackType.tare_g,
          ...(custom ? { sack_name: custom.name } : {}),
          weights: [
            {
              id: newId(),
              gross_g: grams,
              taken_at: at,
              confirmed_twice: w.confirmedTwice,
              supersedes_id: null,
              photo_media_id: scalePhoto,
            },
          ],
          tagged_at: at,
        },
      })
      onSaved()
    } finally {
      setSaving(false)
    }
  }

  const missing =
    red === null
      ? tr('gate.v1.weigh.pickGrade')
      : !sackType
        ? tr('gate.v1.weigh.pickSack')
        : grams === null
          ? tr('gate.v1.weigh.typeWeight')
          : photoRequired && !scalePhoto
            ? tr('gate.v1.weigh.scalePhoto')
            : net !== null && net <= 0
              ? tr('gate.v1.weigh.notMoreThanSack')
              : null
  const cfg = roster.config
  const isRed = red === true

  return (
    <V1Screen
      main={{ padding: '6px 12px', gap: 5 }}
      header={
        <StepHeader step="tag" icon="scale" title={tr('gate.v1.weigh.title')} onBack={onBack} />
      }
      band={
        <>
          {paper && d.paper && (
            <Band tone="paper" icon="book">
              {tr('gate.v1.paper.band', { no: d.paper.slip_no })}
            </Band>
          )}
          <Band tone={isRed ? 'red' : 'good'} icon={isRed ? 'redtie' : 'sack'}>
            {tr(isRed ? 'gate.v1.weigh.bandRed' : 'gate.v1.weigh.bandGood', {
              count: isRed ? reds : good,
              kg: kg(netOf(isRed)),
            })}
          </Band>
        </>
      }
      footer={
        <Footer dense>
          {missing ? (
            <WaitCta height={56}>{missing}</WaitCta>
          ) : paper ? (
            <div style={{ display: 'flex', gap: 8 }}>
              {onSeePage && (
                <div style={{ flex: '0 0 auto', display: 'flex' }}>
                  <Btn icon="book" onClick={onSeePage}>
                    {tr('gate.v1.weigh.seePage')}
                  </Btn>
                </div>
              )}
              <Cta
                label={tr('gate.v1.weigh.savePaper')}
                icon="ok"
                height={56}
                busy={saving}
                onClick={() => void save()}
              />
            </div>
          ) : (
            <Cta
              label={tr(isRed ? 'gate.v1.weigh.saveRed' : 'gate.v1.weigh.saveGood')}
              icon="ok"
              height={56}
              busy={saving}
              onClick={() => void save()}
            />
          )}
        </Footer>
      }
      overlay={
        sheet ? (
          <SackSheet
            value={custom ? null : sack}
            custom={custom}
            onPick={(id) => {
              setCustom(null)
              setSack(id)
              setSheet(false)
            }}
            onCustom={
              typedType
                ? (c) => {
                    setCustom(c)
                    setSheet(false)
                  }
                : undefined
            }
            onClose={() => setSheet(false)}
          />
        ) : null
      }
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 0.92fr) minmax(0, 1.08fr)',
          gap: 6,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '0 8px 0 10px',
            height: 48,
            borderRadius: 12,
            background: C.surface,
            border: `2px solid ${C.ok}`,
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          <Icon name="tag" size={24} style={{ color: C.ok }} />
          <b style={{ fontSize: 17, lineHeight: 1.15, whiteSpace: 'nowrap', flexGrow: 1 }}>
            {tr('gate.v1.weigh.tag', { code: shortCode(scanned.payload) })}
          </b>
          <Tick />
        </div>
        <button
          type="button"
          aria-label={tr('gate.v1.a11y.changeSack')}
          onClick={() => setSheet(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            height: 48,
            padding: '0 6px 0 8px',
            borderRadius: 12,
            background: C.surface,
            border: `1.5px solid ${C.border}`,
            fontFamily: "'Mukta', sans-serif",
            color: C.ink,
            textAlign: 'left',
            boxSizing: 'border-box',
            cursor: 'pointer',
            minWidth: 0,
          }}
        >
          <Icon name="sack" size={24} style={{ color: C.acc }} />
          <span style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
            <b
              style={{
                fontSize: 17,
                lineHeight: 1.15,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {custom
                ? custom.name
                : sackType
                  ? sackName(roster.sackTypes, sackType.id)
                  : tr('gate.v1.weigh.pickSack')}
            </b>
            {sackType && (
              <span
                style={{
                  fontSize: 14,
                  color: C.muted,
                  lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {tr('gate.v1.weigh.sackLine', { kg: kg(sackType.tare_g) })}
              </span>
            )}
          </span>
          <Icon name="down" size={18} />
        </button>
      </div>
      <GradeTiles red={red} onPick={setRed} />
      <Readout
        label={tr(paper ? 'gate.v1.weigh.paperWeight' : 'gate.v1.weigh.weightOf', { n })}
        value={text}
        unit={tr('gate.v1.unit.kg')}
        speakText={
          text ? tr('gate.v1.spoken.kg', { value: text }) : tr('gate.v1.weigh.weightOf', { n })
        }
        side={
          net !== null && net > 0 ? (
            <div
              style={{
                textAlign: 'right',
                display: 'flex',
                flexDirection: 'column',
                flexShrink: 0,
                lineHeight: 1.2,
              }}
            >
              <span style={{ fontSize: 14, color: C.muted }}>{tr('gate.v1.weigh.net')}</span>
              <b style={{ fontSize: 20 }}>
                {kg(net)}
                <span style={{ fontSize: 14, fontWeight: 600 }}> {tr('gate.v1.unit.kg')}</span>
              </b>
            </div>
          ) : undefined
        }
      />
      {w.first !== null && grams === null && (
        <div style={{ fontSize: 16, color: C.bad, fontWeight: 700 }}>
          {tr('gate.v1.weigh.again', {
            min: kg(cfg.weight_min_g),
            max: kg(cfg.weight_max_g),
          })}
        </div>
      )}
      {!paper && (
        <ScalePhoto value={scalePhoto} onChange={setScalePhoto} required={photoRequired} />
      )}
      <Grow />
      <Keypad onKey={onKey} />
    </V1Screen>
  )
}

/** A sack not on the kit list: what the operator calls it and its empty weight (0 kg allowed). */
interface TypedSack {
  name: string
  tare_g: number
}

/** Heaviest empty sack accepted when typed (grams). */
const TYPED_SACK_MAX_G = 5000

function SackSheet({
  value,
  custom,
  onPick,
  onCustom,
  onClose,
}: {
  value: string | null
  custom: TypedSack | null
  onPick: (id: string) => void
  /** Absent when the server has no TYPED_AT_GATE sack yet. */
  onCustom?: (c: TypedSack) => void
  onClose: () => void
}) {
  const { d, roster } = useV1()
  const [other, setOther] = useState(false)
  // A new typed sack always starts empty; the ones typed before are rows of their own.
  const [name, setName] = useState('')
  // Typed in whole grams (an empty sack is light: 0 to 5000 g).
  const [weight, setWeight] = useState('')
  const same = (a: TypedSack, b: TypedSack | null) =>
    !!b && a.name === b.name && a.tare_g === b.tare_g
  // Sacks typed for this farmer's earlier bags, and the one picked now, once each.
  const typed: TypedSack[] = []
  for (const t of [
    ...d.bags.flatMap((b) => (b.sack_name ? [{ name: b.sack_name, tare_g: b.tare_g }] : [])),
    ...(custom ? [custom] : []),
  ]) {
    if (!typed.some((x) => same(x, t))) typed.push(t)
  }
  const tare = weight === '' ? null : Number(weight)
  const ok = name.trim().length > 0 && tare !== null && tare >= 0 && tare <= TYPED_SACK_MAX_G

  if (other && onCustom) {
    return (
      <V1Sheet
        title={tr('gate.v1.sack.otherTitle')}
        onClose={onClose}
        top={140}
        actions={
          <SheetActions>
            <Btn icon="back" onClick={() => setOther(false)}>
              {tr('gate.v1.back')}
            </Btn>
            {ok ? (
              <Cta
                label={tr('gate.v1.save')}
                icon="ok"
                onClick={() => onCustom({ name: name.trim(), tare_g: tare! })}
              />
            ) : (
              <WaitCta>{tr('gate.v1.sack.fill')}</WaitCta>
            )}
          </SheetActions>
        }
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: C.muted }}>
            {tr('gate.v1.sack.name')}
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value.slice(0, 60))}
            autoComplete="off"
            enterKeyHint="next"
            style={sackInput}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: C.muted }}>
            {tr('gate.v1.sack.weight')}
          </span>
          <input
            value={weight}
            onChange={(e) => {
              const v = e.target.value.replace(/[^\d]/g, '').replace(/^0+(?=\d)/, '')
              if (v.length <= 4) setWeight(v)
            }}
            inputMode="numeric"
            autoComplete="off"
            placeholder="0"
            style={sackInput}
          />
          {tare !== null && tare > TYPED_SACK_MAX_G && (
            <span style={{ fontSize: 15, fontWeight: 700, color: C.bad }}>
              {tr('gate.v1.sack.tooHeavy', { g: TYPED_SACK_MAX_G })}
            </span>
          )}
        </label>
        <Warn>{tr('gate.v1.sack.waits')}</Warn>
      </V1Sheet>
    )
  }

  return (
    <V1Sheet title={tr('gate.v1.weigh.sackTitle')} onClose={onClose} top={260}>
      {roster.sackTypes
        .filter((s) => s.code !== TYPED_SACK_CODE)
        .map((s) => (
          <ItemRow
            key={s.id}
            onClick={() => onPick(s.id)}
            border={s.id === value ? C.ink : C.rule}
            borderWidth={s.id === value ? 3 : 1.5}
            padding="8px 12px"
            lead={<Icon name="sack" size={28} style={{ color: C.acc }} />}
            titleSize={18}
            title={sackName(roster.sackTypes, s.id)}
            line={
              <span style={{ fontSize: 15, color: C.muted }}>
                {tr('gate.v1.weigh.sackLine', { kg: kg(s.tare_g) })}
              </span>
            }
          />
        ))}
      {onCustom &&
        typed.map((t) => (
          <ItemRow
            key={`${t.name}|${t.tare_g}`}
            onClick={() => onCustom(t)}
            border={same(t, custom) ? C.ink : C.rule}
            borderWidth={same(t, custom) ? 3 : 1.5}
            padding="8px 12px"
            lead={<Icon name="sack" size={28} style={{ color: C.acc }} />}
            titleSize={18}
            title={t.name}
            line={
              <span style={{ fontSize: 15, color: C.muted }}>
                {tr('gate.v1.weigh.sackLine', { kg: kg(t.tare_g) })} · {tr('gate.v1.sack.typed')}
              </span>
            }
          />
        ))}
      {/* Always offered; a list downloaded before the server had the typed sack says so. */}
      {onCustom ? (
        <ItemRow
          onClick={() => setOther(true)}
          border={C.acc}
          padding="8px 12px"
          lead={<Icon name="pencil" size={26} style={{ color: C.acc }} />}
          titleSize={18}
          title={tr('gate.v1.sack.other')}
          line={
            <span style={{ fontSize: 15, color: C.muted }}>{tr('gate.v1.sack.otherLine')}</span>
          }
        />
      ) : (
        <ItemRow
          border={C.rule}
          padding="8px 12px"
          lead={<Icon name="pencil" size={26} style={{ color: C.muted }} />}
          titleSize={18}
          title={<span style={{ color: C.muted }}>{tr('gate.v1.sack.other')}</span>}
          line={
            <span style={{ fontSize: 15, color: C.bad, fontWeight: 600 }}>
              {tr('gate.v1.sack.needList')}
            </span>
          }
        />
      )}
    </V1Sheet>
  )
}

const sackInput = {
  height: 54,
  width: '100%',
  padding: '0 12px',
  borderRadius: 12,
  border: `2px solid ${C.acc}`,
  background: C.surface,
  fontFamily: "'Mukta', sans-serif",
  fontSize: 22,
  fontWeight: 600,
  color: C.ink,
  outline: 'none',
  boxSizing: 'border-box' as const,
}

/** पिछली बोरी ठीक करें? — the last bag's weight was typed wrong, or it was weighed again. */
export function FixLastSheet({ onClose }: { onClose: () => void }) {
  const { d, dispatch, roster, stamp } = useV1()
  const live = useLiveBags()
  const last = live.at(-1)
  const [reason, setReason] = useState<null | 'typo' | 'reweighed'>(null)
  const [text, setText] = useState('')
  const [scalePhoto, setScalePhoto] = useState<string | null>(null)
  const photoRequired = d.reading_photos_required === true
  const w = useWeightEntry(roster.config.weight_min_g, roster.config.weight_max_g)
  const setW = w.setValue
  const onKey = useCallback(
    (k: string) =>
      setText((c) => {
        const next = typeKey(c, k, { decimals: 2, max: 6 })
        setW(toGrams(next))
        return next
      }),
    [setW],
  )
  if (!last) return null
  const cur = bagWeight(last)
  const grams = toGrams(text)
  const reasonTile = (r: 'typo' | 'reweighed', icon: V1IconName, label: string) => (
    <button
      type="button"
      aria-pressed={reason === r}
      onClick={() => setReason(r)}
      style={{
        position: 'relative',
        minHeight: 64,
        borderRadius: 12,
        border: reason === r ? `3px solid ${C.ink}` : `1.5px solid ${C.border}`,
        background: C.surface,
        color: C.ink,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px 10px',
        gap: 6,
        fontFamily: "'Mukta', sans-serif",
        width: '100%',
        boxSizing: 'border-box',
        cursor: 'pointer',
      }}
    >
      <Icon name={icon} size={26} />
      <b style={{ fontSize: 18 }}>{label}</b>
    </button>
  )
  return (
    <V1Sheet
      title={tr('gate.v1.fix.title')}
      onClose={onClose}
      top={reason ? 40 : 250}
      actions={
        <SheetActions>
          <Btn icon="back" onClick={onClose}>
            {tr('gate.v1.back')}
          </Btn>
          {!reason ? (
            <WaitCta>{tr('gate.v1.pickReason')}</WaitCta>
          ) : grams === null || !w.ready || (photoRequired && !scalePhoto) ? (
            <WaitCta>
              {grams === null || !w.ready
                ? tr('gate.v1.weigh.typeWeight')
                : tr('gate.v1.weigh.scalePhoto')}
            </WaitCta>
          ) : (
            <Cta
              label={tr('gate.v1.save')}
              icon="ok"
              onClick={() => {
                if (photoRequired && !scalePhoto) return
                if (w.askAgain()) {
                  setText('')
                  return
                }
                dispatch({
                  type: 'fix_last_bag',
                  weight: {
                    id: newId(),
                    gross_g: grams,
                    taken_at: stamp(),
                    confirmed_twice: w.confirmedTwice,
                    supersedes_id: null,
                    photo_media_id: scalePhoto,
                  },
                })
                onClose()
              }}
            />
          )}
        </SheetActions>
      }
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 12px',
          borderRadius: 12,
          background: C.surface,
          border: `1.5px solid ${C.rule}`,
        }}
      >
        <Icon name="tag" size={28} style={{ color: C.ok }} />
        <div style={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
            {tr('gate.v1.fix.bag', { n: live.length, code: shortCode(last.qr_payload) })}
          </span>
          <span style={{ fontSize: 15, color: C.muted, lineHeight: 1.3 }}>
            {tr('gate.v1.fix.was')}
          </span>
        </div>
        <span style={{ display: 'flex', alignItems: 'baseline', gap: 4, flexShrink: 0 }}>
          <b style={{ fontSize: 28 }}>{cur ? kg(cur.gross_g) : '—'}</b>
          <span style={{ fontSize: 16, color: C.muted }}>{tr('gate.v1.unit.kg')}</span>
        </span>
      </div>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, lineHeight: 1.25 }}>
        {tr('gate.v1.why')}
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
        {reasonTile('typo', 'keypad', tr('gate.v1.fix.typo'))}
        {reasonTile('reweighed', 'scale', tr('gate.v1.fix.reweighed'))}
      </div>
      <Warn>
        <Rich text={tr('gate.v1.fix.warn')} />
      </Warn>
      {reason && (
        <>
          <Readout label={tr('gate.v1.fix.newWeight')} value={text} unit={tr('gate.v1.unit.kg')} />
          {w.first !== null && grams === null && (
            <div style={{ fontSize: 16, color: C.bad, fontWeight: 700 }}>
              {tr('gate.v1.weigh.again', {
                min: kg(roster.config.weight_min_g),
                max: kg(roster.config.weight_max_g),
              })}
            </div>
          )}
          {d.capture_mode === 'APP' && (
            <ScalePhoto value={scalePhoto} onChange={setScalePhoto} required={photoRequired} />
          )}
          <Keypad onKey={onKey} />
        </>
      )}
    </V1Sheet>
  )
}

/** तौल: कैसे करें — the four steps with pictures, read aloud on सुनें. */
export function WeighHelpSheet({ onClose }: { onClose: () => void }) {
  const steps: [V1IconName, string, string][] = [
    ['sack', tr('gate.v1.help.step1'), tr('gate.v1.help.photo1')],
    ['probe', tr('gate.v1.help.step2'), tr('gate.v1.help.photo2')],
    ['tag', tr('gate.v1.help.step3'), tr('gate.v1.help.photo3')],
    ['scale', tr('gate.v1.help.step4'), tr('gate.v1.help.photo4')],
  ]
  const tips = [tr('gate.v1.help.tip1'), tr('gate.v1.help.tip2'), tr('gate.v1.help.tip3')]
  return (
    <V1Sheet
      title={tr('gate.v1.help.weighTitle')}
      onClose={onClose}
      top={40}
      actions={<Cta label={tr('gate.v1.help.gotIt')} icon="ok" onClick={onClose} />}
    >
      <button
        type="button"
        onClick={() => speak(sentences(steps.map((s) => s[1])))}
        style={{
          minHeight: 52,
          borderRadius: 12,
          border: 0,
          background: C.help,
          color: '#ffffff',
          fontFamily: "'Mukta', sans-serif",
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          cursor: 'pointer',
        }}
      >
        <Icon name="speaker" size={26} />
        <b style={{ fontSize: 19 }}>{tr('gate.v1.listen')}</b>
      </button>
      {steps.map(([icon, text, label], i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span
            style={{
              width: 30,
              height: 30,
              flexShrink: 0,
              borderRadius: 15,
              border: `2px solid ${C.ink}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 16,
              boxSizing: 'border-box',
            }}
          >
            {i + 1}
          </span>
          <span
            role="img"
            aria-label={label}
            style={{
              width: 76,
              height: 58,
              flexShrink: 0,
              borderRadius: 8,
              background: C.photo,
              color: C.photoInk,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name={icon} size={30} sw={1.6} />
          </span>
          <span style={{ fontSize: 17, lineHeight: 1.3 }}>{text}</span>
        </div>
      ))}
      <div
        style={{
          borderRadius: 12,
          background: C.warnWash,
          padding: '8px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <b style={{ fontSize: 17, color: C.accDark }}>{tr('gate.v1.help.trouble')}</b>
        {tips.map((t, i) => (
          <span key={i} style={{ fontSize: 16 }}>
            <Rich text={t} />
          </span>
        ))}
      </div>
    </V1Sheet>
  )
}

function ScalePhoto({
  value,
  onChange,
  required = false,
}: {
  value: string | null
  onChange: (id: string | null) => void
  required?: boolean
}) {
  return (
    <OptionalPhoto
      value={value}
      onChange={onChange}
      label={tr('gate.v1.weigh.scalePhoto')}
      takenLabel={tr('gate.v1.weigh.scalePhotoTaken')}
      retakeLabel={tr('gate.v1.weigh.scalePhotoRetake')}
      required={required}
    />
  )
}
